// Thin wrapper over the SDK's JSON-RPC client (sdk/ts/src/relayer.ts): the
// app re-uses `Rpc` as-is (retries, evidence log, sendV1 etc. are already
// exactly what the app needs) and adds the two read methods the SDK does
// not carry because the SDK is chain-agnostic about "what this app shows on
// Home" - `getVersion` (Verify page, 5.10) and `getTokenAccountsByOwner`
// (Home's per-token list, 4). relayer.ts is not part of index.ts's barrel
// (see the comment in vite.config.ts), so it is imported through the
// `@thevault/sdk/*` subpath alias instead of the bare `@thevault/sdk` specifier.
import { getBase58Decoder, getBase64Encoder } from '@solana/kit';
import type { TransactionJson } from '@thevault/sdk';
import { Rpc as SdkRpc } from '@thevault/sdk/relayer';

export {
  EvidenceLog,
  type EvidenceEntry,
  type LatestBlockhash,
  type OnChainAccount,
  RelayerError,
  type RelayerErrorCode,
  type RelayerSigner,
  type RpcConfig,
  type SendResult,
  type SignatureStatus,
  type SimulationResult,
  type TransactionError,
  VAULT_ERROR_CEILING,
  fromBase58,
  isFinalizedSuccess,
  isRouteFailureError,
  relayerFromSeed,
  sendV1,
  sendV1Unchecked,
  signV1,
  toBase58,
  transactionError,
} from '@thevault/sdk/relayer';

// @solana/kit names a base64<->bytes codec by the *value* side (a string),
// same as relayer.ts's own private helper: the "encoder" turns a base64
// string into bytes, the "decoder" turns bytes into a base64 string.
function fromBase64(s: string): Uint8Array {
  return new Uint8Array(getBase64Encoder().encode(s));
}

/** SPL Token / Token-2022 base account layout (165 bytes): mint, owner, amount are the first three fields, identical in both programs. */
export interface TokenAccountInfo {
  pubkey: string;
  mint: string;
  owner: string;
  /** Raw units; decimals are not part of the token *account* layout and are fetched from the mint separately, on demand (getMintDecimals). */
  amount: bigint;
}

function parseTokenAccount(pubkey: string, data: Uint8Array): TokenAccountInfo {
  const b58 = getBase58Decoder();
  const mint = b58.decode(data.slice(0, 32));
  const owner = b58.decode(data.slice(32, 64));
  const view = new DataView(data.buffer, data.byteOffset + 64, 8);
  const amount = view.getBigUint64(0, true);
  return { pubkey, mint, owner, amount };
}

export interface VersionInfo {
  'solana-core': string;
  'feature-set'?: number;
}

/** The SDK's `Rpc` plus the app-only read methods `getVersion` and `getTokenAccountsByOwner`. */
export class Rpc extends SdkRpc {
  async getVersion(): Promise<VersionInfo> {
    return this.call<VersionInfo>('getVersion', []);
  }

  /** Every token account `owner` holds under `programId` (SPL Token or Token-2022), base64-decoded. */
  async getTokenAccountsByOwner(owner: string, programId: string, commitment = 'confirmed'): Promise<TokenAccountInfo[]> {
    const res = await this.call<{
      context: { slot: number };
      value: Array<{ pubkey: string; account: { data: [string, string]; owner: string; lamports: number } }>;
    }>('getTokenAccountsByOwner', [owner, { programId }, { encoding: 'base64', commitment }]);
    return res.value.map(({ pubkey, account }) => parseTokenAccount(pubkey, fromBase64(account.data[0])));
  }

  /** A mint's `decimals` field (SPL Token / Token-2022 mint layout, byte 44), fetched on demand since token accounts do not carry it. */
  async getMintDecimals(mint: string): Promise<number> {
    const acc = await this.getAccountInfo(mint);
    if (!acc) throw new RangeError(`mint ${mint} not found`);
    if (acc.data.length < 45) throw new RangeError(`mint ${mint} account data too short for a mint (${acc.data.length} bytes)`);
    return acc.data[44]!;
  }

  /** One `getSignatureStatuses` read, history included: null when the network has no record of the signature (yet). Used by the engine's `reconcile` (R45) and by the activation's Check again, which reads a null entry as no evidence (docs/18 5.1 D step 4). */
  async signatureStatus(signature: string): Promise<{ err: unknown; confirmationStatus?: string; slot?: number } | null> {
    const res = await this.call<{ value: ({ err: unknown; confirmationStatus?: string; slot?: number } | null)[] }>('getSignatureStatuses', [[signature], { searchTransactionHistory: true }]);
    return res.value[0] ?? null;
  }

  /**
   * The list form (R69, docs/17 2.3, E1): one `getSignatureStatuses` call for every signature, history included, one
   * entry per signature in order, each null when the network has no record of it (yet). Used by the reconciliation
   * step's evidence reader, which checks the answer's shape itself.
   */
  async signatureStatuses(signatures: string[]): Promise<readonly ({ err: unknown; confirmationStatus?: string; slot?: number } | null)[]> {
    const res = await this.call<{ value: ({ err: unknown; confirmationStatus?: string; slot?: number } | null)[] }>('getSignatureStatuses', [signatures, { searchTransactionHistory: true }]);
    return res.value;
  }

  /** `getTransaction` with `json` encoding, as the swap receipt reads it (sdk/ts/src/receipt.ts, R46): account keys, token balance changes and inner instructions. Null when the endpoint has no record of it (yet). */
  async getTransactionJson(signature: string): Promise<TransactionJson | null> {
    return this.call<TransactionJson | null>('getTransaction', [signature, { encoding: 'json', maxSupportedTransactionVersion: 1, commitment: 'confirmed' }]);
  }
}
